/* ==========================================================================
   custom-pdp.js — Sothiares product page rebuild
   --------------------------------------------------------------------------
   Behaviour for sections/custom-pdp.liquid:
     1. Gallery — arrows, thumbnail rail, counter
     2. Sticky tab bar — click to jump, scroll-spy to track the active section
     3. Size selection — keeps the hidden variant id, price and button in sync
     4. Mobile sticky add-to-cart — reveals once the inline button leaves view

   No dependencies. Everything is scoped to a single [data-cpdp] root so more
   than one instance on a page cannot collide.

   Author: Custom development team
   Created: 2026-09-04
   ========================================================================== */

(function () {
  'use strict';

  function initGallery(root) {
    var slides = root.querySelector('[data-cpdp-slides]');
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-cpdp-thumb]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-cpdp-dot]'));
    var counter = root.querySelector('[data-cpdp-counter]');
    if (!slides) return;

    var total = slides.children.length;
    if (!total) return;
    var index = 0;

    // Mobile swipes natively via scroll-snap; desktop steps with transform.
    // Reading the computed overflow keeps the two in sync with the stylesheet
    // rather than duplicating the breakpoint here.
    function isSwipe() {
      return getComputedStyle(slides).overflowX !== 'visible';
    }

    function paint() {
      thumbs.forEach(function (t, i) {
        t.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      dots.forEach(function (d, i) {
        d.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (counter) counter.textContent = index + 1 + ' / ' + total;
    }

    function show(next) {
      index = (next + total) % total;
      if (isSwipe()) {
        slides.style.transform = '';
        slides.scrollTo({
          left: index * slides.clientWidth,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
        });
      } else {
        slides.style.transform = 'translateX(' + -index * 100 + '%)';
      }
      paint();
    }

    var prev = root.querySelector('[data-cpdp-prev]');
    var next = root.querySelector('[data-cpdp-next]');
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { show(i); });
    });

    // Keep the indicator honest when the swipe came from a finger, not a click.
    var settle;
    slides.addEventListener('scroll', function () {
      if (!isSwipe()) return;
      clearTimeout(settle);
      settle = setTimeout(function () {
        var w = slides.clientWidth;
        if (!w) return;
        index = Math.max(0, Math.min(total - 1, Math.round(slides.scrollLeft / w)));
        paint();
      }, 80);
    }, { passive: true });

    // Swapping between layouts leaves the wrong positioning technique applied.
    window.addEventListener('resize', function () { show(index); });

    show(0);
  }

  function initBack(root) {
    var back = root.querySelector('[data-cpdp-back]');
    if (!back) return;
    // Nothing to go back to on a cold entry; send them to the collection instead.
    if (history.length <= 1) {
      var fallback = back.getAttribute('data-fallback');
      if (fallback) back.setAttribute('href', fallback);
    }
    back.addEventListener('click', function (event) {
      if (history.length > 1) {
        event.preventDefault();
        history.back();
      }
    });
  }

  function initTabs(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-cpdp-tab]'));
    if (!tabs.length) return;

    var targets = tabs
      .map(function (tab) {
        var el = root.querySelector('#' + tab.getAttribute('data-cpdp-tab'));
        return el ? { tab: tab, el: el } : null;
      })
      .filter(Boolean);

    if (!targets.length) return;

    targets.forEach(function (pair) {
      pair.tab.addEventListener('click', function () {
        // A tab may point at an accordion row; open it before scrolling, or
        // the jump lands on a closed summary and looks like nothing happened.
        if (pair.el.tagName === 'DETAILS') pair.el.open = true;

        // A tab may also scroll somewhere else while opening a related row —
        // Fabric & Fit lands on the material trio but opens Specifications.
        var alsoOpen = pair.tab.getAttribute('data-cpdp-open');
        if (alsoOpen) {
          var extra = root.querySelector('#' + alsoOpen);
          if (extra && extra.tagName === 'DETAILS') extra.open = true;
        }
        pair.el.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    });

    function setActive(el) {
      targets.forEach(function (pair) {
        pair.tab.setAttribute('aria-selected', pair.el === el ? 'true' : 'false');
      });
    }

    // Track whichever section currently occupies the upper part of the viewport.
    if ('IntersectionObserver' in window) {
      var visible = new Map();
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            visible.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
          });
          var best = null;
          var bestRatio = 0;
          visible.forEach(function (ratio, el) {
            if (ratio > bestRatio) {
              bestRatio = ratio;
              best = el;
            }
          });
          if (best) setActive(best);
        },
        { rootMargin: '-64px 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] }
      );
      targets.forEach(function (pair) { observer.observe(pair.el); });
    }

    setActive(targets[0].el);
  }

  function initVariants(root) {
    var form = root.querySelector('[data-cpdp-form]');
    if (!form) return;

    var idInput = form.querySelector('[data-cpdp-variant-id]');
    var priceEl = root.querySelector('[data-cpdp-price]');
    var stickyPriceEl = root.querySelector('[data-cpdp-sticky-price]');
    var stickySizeEl = root.querySelector('[data-cpdp-sticky-size]');
    var submits = Array.prototype.slice.call(root.querySelectorAll('[data-cpdp-submit]'));
    var dataEl = root.querySelector('[data-cpdp-variants]');
    if (!idInput || !dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      // A malformed payload should leave the native form working rather than
      // throwing and killing the rest of the page's behaviour.
      console.error('[custom-pdp] could not parse variant data', e);
      return;
    }

    function apply(variant) {
      if (!variant) return;
      idInput.value = variant.id;
      // price_html carries the anniversary sale markup when the sale is set up;
      // it comes from our own Liquid snippet, not from user input.
      if (priceEl && variant.price_html) priceEl.innerHTML = variant.price_html;
      else if (priceEl && variant.price_formatted) priceEl.textContent = variant.price_formatted;
      if (stickyPriceEl && variant.price_html) stickyPriceEl.innerHTML = variant.price_html;
      else if (stickyPriceEl && variant.price_formatted) stickyPriceEl.textContent = variant.price_formatted;
      if (stickySizeEl && variant.option_label) stickySizeEl.textContent = 'Size ' + variant.option_label;
      submits.forEach(function (btn) {
        btn.disabled = !variant.available;
        btn.textContent = variant.available
          ? btn.getAttribute('data-label-available')
          : btn.getAttribute('data-label-soldout');
      });
    }

    form.addEventListener('change', function (event) {
      if (!event.target.matches('[data-cpdp-size]')) return;
      var match = variants.filter(function (v) {
        return String(v.id) === String(event.target.value);
      })[0];
      apply(match);
    });

    var checked = form.querySelector('[data-cpdp-size]:checked');
    if (checked) {
      apply(
        variants.filter(function (v) {
          return String(v.id) === String(checked.value);
        })[0]
      );
    }
  }

  function initStickyBar(root) {
    var bar = root.querySelector('[data-cpdp-stickybar]');
    var anchor = root.querySelector('[data-cpdp-submit]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.setAttribute('data-visible', entry.isIntersecting ? 'false' : 'true');
        });
      },
      { threshold: 0 }
    );
    observer.observe(anchor);
  }

  /* ------------------------------------------------------------------------
     Recommendation card titles
     ------------------------------------------------------------------------
     Sothiares colourways are separate products sharing a base title before a
     pipe ("Technical Fur Collar Field Jacket | Navy"). Inline, repeated down a
     row of four, that suffix is the loudest thing on the card.

     The reference sets a card in three lines — name, then a quieter line
     naming the cloth, then the price. We have no cloth field, but the
     colourway sits naturally in that second line, so the suffix is promoted
     out of the title rather than dropped.

     Done in script rather than Liquid because the card is the theme's own
     product-card block, which is not ours to edit. The file is deferred, so
     this runs after parse and before first paint.

     Scoped hard to the recommendations grid — the main product title above it
     is a different element and keeps its own formatting.
     ------------------------------------------------------------------------ */
  function initCardTitles() {
    var grid = document.querySelector('[id$="__recommendations"]');
    if (!grid) return;

    grid.querySelectorAll('.resource-list__item .text-block').forEach(function (block) {
      if (block.hasAttribute('data-cpdp-split')) return;

      // Walk text nodes rather than rewriting innerHTML: the title sits inside
      // a link the theme wires up, and replacing the markup would drop it.
      // Collected up front because the loop below replaces these nodes, and a
      // TreeWalker sitting on a node that has just left the tree is not
      // guaranteed to find its way to the next one.
      var walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
      var nodes = [];
      var next;
      while ((next = walker.nextNode())) nodes.push(next);

      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        if (node.nodeValue.indexOf('|') === -1) continue;

        var parts = node.nodeValue.split('|');
        var base = parts.shift().trim();
        var colour = parts.join('|').trim();
        // Guard against a title that opens with the pipe — better the full
        // string than an empty card.
        if (!base) continue;

        // The name is wrapped in its own element so it can reserve two lines
        // whether or not it needs them. Reserving that space on the block
        // instead would not work: the block holds the whole text side of the
        // card, so a name that wraps would still push the price below its
        // neighbours'.
        var parent = node.parentNode;
        if (!parent) continue;

        var nameEl = document.createElement('span');
        nameEl.className = 'cpdp-card__name';
        nameEl.textContent = base;
        parent.replaceChild(nameEl, node);

        // The line is written even when there is no colourway — empty, but
        // still occupying its line. A card without one would otherwise pull
        // its price up a line and break the row's shared baseline, which is
        // reachable here: the grid draws four from a collection of twenty-one
        // and some of those products have no colourway at all.
        var line = document.createElement('span');
        line.className = 'cpdp-card__colour';
        line.textContent = colour;
        nameEl.insertAdjacentElement('afterend', line);
      }

      block.setAttribute('data-cpdp-split', '');
    });
  }

  /* ------------------------------------------------------------------------
     Complete-the-look rail
     ------------------------------------------------------------------------
     Chevrons that step the rail one card at a time, following the reference.
     They ship hidden and are only revealed once the rail is measured, so a
     rail that happens to fit its column never shows a control that would do
     nothing.
     ------------------------------------------------------------------------ */
  function initLookRail(root) {
    var wrap = root.querySelector('.cpdp__rail-wrap');
    if (!wrap) return;

    var rail = wrap.querySelector('.cpdp__rail');
    var prev = wrap.querySelector('[data-cpdp-rail="prev"]');
    var next = wrap.querySelector('[data-cpdp-rail="next"]');
    if (!rail || !prev || !next) return;

    function step() {
      var card = rail.querySelector('.cpdp__card');
      if (!card) return rail.clientWidth;
      // One card plus the gap. Reading the gap off the element rather than
      // hardcoding it keeps this correct if the spacing changes.
      var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function sync() {
      // 1px of slack: sub-pixel widths mean scrollLeft rarely lands exactly on
      // the maximum, which would leave the next chevron visible at the end.
      var max = rail.scrollWidth - rail.clientWidth;
      var overflows = max > 1;
      prev.hidden = !overflows || rail.scrollLeft <= 1;
      next.hidden = !overflows || rail.scrollLeft >= max - 1;
    }

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

    function go(delta) {
      var from = rail.scrollLeft;
      var max = rail.scrollWidth - rail.clientWidth;
      var to = Math.max(0, Math.min(from + delta, max));
      if (Math.abs(to - from) < 1) return;

      rail.scrollTo({ left: to, behavior: reduce.matches ? 'auto' : 'smooth' });

      // Some engines drop a smooth scroll entirely and leave the rail exactly
      // where it was, which makes the chevron look dead — measured happening
      // here, where only behavior:'instant' moved the element at all. If
      // nothing has shifted shortly after, place it outright. A working smooth
      // scroll is already past `from` by this point, so this does not fight it.
      setTimeout(function () {
        if (Math.abs(rail.scrollLeft - from) < 1) {
          rail.scrollTo({ left: to, behavior: 'instant' });
          sync();
        }
      }, 250);
    }

    prev.addEventListener('click', function () {
      go(-step());
    });

    next.addEventListener('click', function () {
      go(step());
    });

    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });

    // Images are lazy-loaded, so scrollWidth is not final at DOMContentLoaded.
    sync();
    window.addEventListener('load', sync);
  }

  function init(root) {
    if (root.hasAttribute('data-cpdp-ready')) return;
    root.setAttribute('data-cpdp-ready', '');
    initGallery(root);
    initBack(root);
    initTabs(root);
    initVariants(root);
    initStickyBar(root);
    initLookRail(root);
  }

  function boot() {
    document.querySelectorAll('[data-cpdp]').forEach(init);
    initCardTitles();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // The theme editor re-renders sections in place; re-init the new markup.
  document.addEventListener('shopify:section:load', boot);
})();
